/**
 * ProtectedRoute — gate consultant-facing routes behind Firebase Auth
 *
 * Usage in App.tsx:
 *   <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
 *
 * Behaviour:
 *   Loading  → show spinner (Firebase resolving initial auth state)
 *   No user  → redirect to /login (preserves attempted URL in state)
 *   Not provisioned (user but no platformUser) → show "not provisioned" screen
 *   Provisioned → render children
 *
 * Optional props:
 *   requireRole — restrict to specific roles (e.g. requireRole="clutch_admin")
 *                 Defaults to any provisioned user.
 */

import { type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { signOut } from 'firebase/auth'
import { auth } from '../lib/firebase'
import { useAuth, type UserRole } from '../context/AuthContext'
import styles from './ProtectedRoute.module.css'

interface Props {
  children:    ReactNode
  requireRole?: UserRole | UserRole[]
}

export function ProtectedRoute({ children, requireRole }: Props) {
  const { user, platformUser, loading } = useAuth()
  const location = useLocation()

  // 1. Firebase still resolving auth state
  if (loading) {
    return (
      <div className={styles.center}>
        <div className={styles.spinner} />
      </div>
    )
  }

  // 2. Not signed in at all → /login
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // 3. Signed in but not provisioned in platform_users
  if (!platformUser) {
    return (
      <div className={styles.center}>
        <div className={styles.notProvisioned}>
          <div className={styles.npIcon}>⚠</div>
          <h2 className={styles.npTitle}>Account not provisioned</h2>
          <p className={styles.npBody}>
            You're signed in as <strong>{user.email}</strong>, but your account
            hasn't been added to the Clutch OCM platform yet.
          </p>
          <p className={styles.npBody}>
            Contact your Clutch administrator to request access.
          </p>
          <button
            className={styles.signOutBtn}
            onClick={() => signOut(auth)}
          >
            Sign out
          </button>
        </div>
      </div>
    )
  }

  // 4. Role restriction check
  if (requireRole) {
    const allowed = Array.isArray(requireRole) ? requireRole : [requireRole]
    if (!allowed.includes(platformUser.role)) {
      return (
        <div className={styles.center}>
          <div className={styles.notProvisioned}>
            <div className={styles.npIcon}>🔒</div>
            <h2 className={styles.npTitle}>Access restricted</h2>
            <p className={styles.npBody}>
              Your role (<strong>{platformUser.role}</strong>) doesn't have
              permission to view this page.
            </p>
            <Navigate to="/dashboard" replace />
          </div>
        </div>
      )
    }
  }

  // 5. All checks pass
  return <>{children}</>
}
